import type { AthleteProfile, PreferredUnits } from "../features/athlete-profile/types/athleteProfile";

const POUNDS_TO_KILOGRAMS = 0.45359237;
const KILOGRAMS_TO_POUNDS = 1 / POUNDS_TO_KILOGRAMS;
const INCHES_TO_CENTIMETERS = 2.54;
const CENTIMETERS_TO_INCHES = 1 / INCHES_TO_CENTIMETERS;
const FAHRENHEIT_OFFSET = 32;
const FAHRENHEIT_SCALE = 5 / 9;
const CELSIUS_TO_FAHRENHEIT_SCALE = 9 / 5;
const MILLILITERS_PER_OUNCE = 29.5735;
const METERS_TO_MILES = 1 / 1609.344;
const METERS_TO_KILOMETERS = 1 / 1000;
const METERS_TO_FEET = 3.280839895013123;
const METERS_PER_SECOND_TO_MPH = 2.2369362921;
const METERS_PER_SECOND_TO_KPH = 3.6;
const MILES_PER_HOUR_TO_KPH = 1.60934;

type NumberOptions = {
  decimals?: number;
  trimTrailingZeros?: boolean;
};

function formatNumber(value: number, options: NumberOptions = {}): string {
  const decimals = options.decimals ?? 0;
  const fixed = value.toFixed(decimals);
  if (!options.trimTrailingZeros) {
    return fixed;
  }
  return fixed.replace(/\.0+$|(?<=\.[0-9]*[1-9])0+$/u, "");
}

export function kilogramsToPounds(value: number): number {
  return value * KILOGRAMS_TO_POUNDS;
}

export function poundsToKilograms(value: number): number {
  return value * POUNDS_TO_KILOGRAMS;
}

export function inchesToCentimeters(value: number): number {
  return value * INCHES_TO_CENTIMETERS;
}

export function centimetersToInches(value: number): number {
  return value * CENTIMETERS_TO_INCHES;
}

export function fahrenheitToCelsius(value: number): number {
  return (value - FAHRENHEIT_OFFSET) * FAHRENHEIT_SCALE;
}

export function celsiusToFahrenheit(value: number): number {
  return (value * CELSIUS_TO_FAHRENHEIT_SCALE) + FAHRENHEIT_OFFSET;
}

export function formatWeightFromKilograms(weightKg: number, units: PreferredUnits, decimals = 1): string {
  if (units === "metric") {
    return `${formatNumber(weightKg, { decimals })} kg`;
  }
  return `${formatNumber(kilogramsToPounds(weightKg), { decimals })} lb`;
}

export function formatWeightFromPounds(weightLb: number, units: PreferredUnits, decimals = 1): string {
  if (units === "metric") {
    return `${formatNumber(poundsToKilograms(weightLb), { decimals })} kg`;
  }
  return `${formatNumber(weightLb, { decimals })} lb`;
}

export function formatTargetWeightRangeFromPounds(range: AthleteProfile["personal"]["targetWeightLb"], units: PreferredUnits): string {
  if (units === "metric") {
    return `${formatNumber(poundsToKilograms(range.minimum), { decimals: 1 })}-${formatNumber(poundsToKilograms(range.maximum), { decimals: 1 })} kg`;
  }
  return `${range.minimum}-${range.maximum} lb`;
}

export function formatHeightFromInches(heightInches: number, units: PreferredUnits): string {
  if (units === "metric") {
    return `${Math.round(inchesToCentimeters(heightInches))} cm`;
  }
  const feet = Math.floor(heightInches / 12);
  const inches = Math.round(heightInches % 12);
  return `${feet}' ${inches}"`;
}

export function formatHeightFromCentimeters(heightCm: number, units: PreferredUnits): string {
  if (units === "metric") {
    return `${Math.round(heightCm)} cm`;
  }
  return formatHeightFromInches(centimetersToInches(heightCm), units);
}

export function formatTemperatureFromFahrenheit(temperatureF: number, units: PreferredUnits, decimals = 1): string {
  if (units === "metric") {
    return `${formatNumber(fahrenheitToCelsius(temperatureF), { decimals })} C`;
  }
  return `${formatNumber(temperatureF, { decimals: 0 })} F`;
}

export function formatTemperatureFromCelsius(temperatureC: number, units: PreferredUnits, decimals = 1): string {
  if (units === "metric") {
    return `${formatNumber(temperatureC, { decimals })} C`;
  }
  return `${formatNumber(celsiusToFahrenheit(temperatureC), { decimals: 0 })} F`;
}

export function formatDistanceFromMeters(distanceMeters: number, units: PreferredUnits, decimals = 1): string {
  if (units === "metric") {
    return `${formatNumber(distanceMeters * METERS_TO_KILOMETERS, { decimals })} km`;
  }
  return `${formatNumber(distanceMeters * METERS_TO_MILES, { decimals })} mi`;
}

export function formatSpeedFromMetersPerSecond(speedMetersPerSecond: number, units: PreferredUnits, decimals = 1): string {
  if (units === "metric") {
    return `${formatNumber(speedMetersPerSecond * METERS_PER_SECOND_TO_KPH, { decimals })} km/h`;
  }
  return `${formatNumber(speedMetersPerSecond * METERS_PER_SECOND_TO_MPH, { decimals })} mph`;
}

export function formatWindSpeedFromMph(speedMph: number, units: PreferredUnits, decimals = 0): string {
  if (units === "metric") {
    return `${formatNumber(speedMph * MILES_PER_HOUR_TO_KPH, { decimals })} km/h`;
  }
  return `${formatNumber(speedMph, { decimals })} mph`;
}

export function formatElevationFromMeters(elevationMeters: number, units: PreferredUnits): string {
  if (units === "metric") {
    return `${formatNumber(elevationMeters, { decimals: 0 })} m`;
  }
  return `${formatNumber(elevationMeters * METERS_TO_FEET, { decimals: 0 })} ft`;
}

export function formatFluidsMlPerHour(fluidsMlPerHour: number, units: PreferredUnits): string {
  if (units === "metric") {
    return `${Math.round(fluidsMlPerHour)} ml/hr`;
  }
  return `${formatNumber(fluidsMlPerHour / MILLILITERS_PER_OUNCE, { decimals: 1 })} oz/hr`;
}

export function formatDailyFluids(fluidsMl: number, units: PreferredUnits): string {
  if (units === "metric") {
    return `${Math.round(fluidsMl)} ml/day`;
  }
  return `${formatNumber(fluidsMl / MILLILITERS_PER_OUNCE, { decimals: 0 })} oz/day`;
}

export function formatMinutesAsDuration(totalMinutes: number): string {
  const wholeMinutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(wholeMinutes / 60);
  const minutes = wholeMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
